'use client';

import React, { useState, useEffect } from 'react';
import { Slider } from '../ui/slider';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { useSelector, useDispatch } from 'react-redux';
import FilterSkeleton from '../skeleton/FilterSkeleton';
import { debounce, formatCurrency } from '@/lib/utils';
import {
  applyFilter,
  applyReturnFilter,
  resetFilter,
  filterFlights,
} from '@flysync/shared-redux/slices/filterSlice';
import {
  setFilteredOnwardData,
  setFilteredReturnData,
} from '@flysync/shared-redux/slices/flightDetails';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { clearLocalState } from '@flysync/shared-redux/slices/flightSearchSlice';

function Filter({ data, isLoading }: { data: any; isLoading: boolean }) {
  const [tabIndex, setTabIndex] = useState(0);
  const dispatch = useDispatch();
  const flightSearchInput = useSelector((state: any) => state.flightSearch);
  const userInput = useSelector((state: any) => state.userInputs);

  // Get current filter state from Redux
  const currentFilterState = useSelector((state: any) => state.filter);

  // Get filter data based on selected tab
  const filterData =
    tabIndex === 0 ? data?.outBoundFlight?.filter : data?.returnFlight?.filter;
  const flightListData =
    tabIndex === 0 ? data?.outBoundFlight?.data : data?.returnFlight?.data;

  const stopsData = filterData?.stops;
  const departureTime = filterData?.departure_time;
  const priceRange = filterData?.priceRange;
  const airlines = filterData?.airline;

  // Initialize with Redux state or default values
  const [selectedAirline, setSelectedAirline] = React.useState<string[]>(
    currentFilterState?.airline || [],
  );
  const selectedPriceRange = React.useRef<number>(
    currentFilterState?.priceRange?.[1] || priceRange?.max || 0,
  );

  const [sliderValue, setSliderValue] = useState<number>(
    currentFilterState?.priceRange?.[1] ||
    (tabIndex === 0
      ? data?.outBoundFlight?.filter?.priceRange?.max || 0
      : data?.returnFlight?.filter?.priceRange?.max || 0),
  );

  const [outboundDeparture, setOutboundDeparture] = useState<string[]>(
    currentFilterState?.departure_time || [],
  );
  const [returnDeparture, setReturnDeparture] = useState<string[]>(
    currentFilterState?.departure_time || [],
  );

  // Get current departure times based on tab
  const selectedDeparture =
    tabIndex === 0 ? outboundDeparture : returnDeparture;
  const setSelectedDeparture =
    tabIndex === 0 ? setOutboundDeparture : setReturnDeparture;

  // Separate states for outbound and return stops
  const [outboundStops, setOutboundStops] = useState<string[]>(
    currentFilterState?.stops || [],
  );
  const [returnStops, setReturnStops] = useState<string[]>(
    currentFilterState?.stops || [],
  );

  // Get current stops based on tab
  const selectedStop = tabIndex === 0 ? outboundStops : returnStops;
  const setSelectedStops = tabIndex === 0 ? setOutboundStops : setReturnStops;

  const onwardFlight = useSelector(
    (state: any) => state.flightDetails.onwardFlight,
  );
  const returnFlight = useSelector(
    (state: any) => state.flightDetails.returnFlight,
  );

  // Sync local state with Redux state when component mounts or filter state changes
  useEffect(() => {
    // Update airline selection
    if (currentFilterState?.airline && currentFilterState.airline.length > 0) {
      setSelectedAirline(currentFilterState.airline);
    }

    // Update price range - Always update slider value from Redux state
    if (
      currentFilterState?.priceRange &&
      currentFilterState.priceRange.length > 0
    ) {
      const maxPrice = currentFilterState.priceRange[1];
      setSliderValue(maxPrice);
      selectedPriceRange.current = maxPrice;
    } else {
      // If no Redux state, reset to default max price
      const defaultMax =
        tabIndex === 0
          ? data?.outBoundFlight?.filter?.priceRange?.max || 0
          : data?.returnFlight?.filter?.priceRange?.max || 0;
      setSliderValue(defaultMax);
      selectedPriceRange.current = defaultMax;
    }

    // Update departure times
    if (
      currentFilterState?.departure_time &&
      currentFilterState.departure_time.length > 0
    ) {
      setOutboundDeparture(currentFilterState.departure_time);
      setReturnDeparture(currentFilterState.departure_time);
    }

    // Update stops
    if (currentFilterState?.stops && currentFilterState.stops.length > 0) {
      setOutboundStops(currentFilterState.stops);
      setReturnStops(currentFilterState.stops);
    }
  }, [currentFilterState, tabIndex, data]);

  // Restore filters from session storage on component mount (fallback)
  useEffect(() => {
    const restoreFiltersFromStorage = () => {
      try {
        // Try to restore from Redux state first
        if (
          currentFilterState?.airline?.length > 0 ||
          currentFilterState?.priceRange?.length > 0 ||
          currentFilterState?.departure_time?.length > 0 ||
          currentFilterState?.stops?.length > 0
        ) {
          return; // Redux state already has values, don't override
        }

        // Fallback to session storage
        const onwardFilterStr = sessionStorage.getItem('onwardFilter');
        if (onwardFilterStr) {
          const onwardFilter = JSON.parse(onwardFilterStr);

          if (onwardFilter.airline?.length > 0) {
            setSelectedAirline(onwardFilter.airline);
          }
          if (onwardFilter.priceRange?.length > 0) {
            const maxPrice = onwardFilter.priceRange[1];
            setSliderValue(maxPrice);
            selectedPriceRange.current = maxPrice;
          } else {
            // If no price range in session storage, reset to default
            const defaultMax =
              tabIndex === 0
                ? data?.outBoundFlight?.filter?.priceRange?.max || 0
                : data?.returnFlight?.filter?.priceRange?.max || 0;
            setSliderValue(defaultMax);
            selectedPriceRange.current = defaultMax;
          }
          if (onwardFilter.departure_time?.length > 0) {
            setOutboundDeparture(onwardFilter.departure_time);
            setReturnDeparture(onwardFilter.departure_time);
          }
          if (onwardFilter.stops?.length > 0) {
            setOutboundStops(onwardFilter.stops);
            setReturnStops(onwardFilter.stops);
          }
        }
      } catch (error) {
        console.warn('Error restoring filters from storage:', error);
      }
    };

    restoreFiltersFromStorage();
  }, []); // Run only on mount

  // Additional effect to handle mobile popup reopening
  useEffect(() => {
    // This effect runs when the component re-renders (like when mobile popup reopens)
    // and ensures the slider value is always in sync with Redux state
    if (
      currentFilterState?.priceRange &&
      currentFilterState.priceRange.length > 0
    ) {
      const maxPrice = currentFilterState.priceRange[1];
      // Only update if the value is different to avoid unnecessary re-renders
      if (sliderValue !== maxPrice) {
        setSliderValue(maxPrice);
        selectedPriceRange.current = maxPrice;
      }
    }
  }, [currentFilterState?.priceRange, sliderValue]);

  const handleResetFilter = () => {
    // Reset all local state
    setSelectedAirline([]);
    setOutboundDeparture([]);
    setReturnDeparture([]);
    setOutboundStops([]);
    setReturnStops([]);

    // Reset slider value to max
    const currentMax =
      tabIndex === 0
        ? data?.outBoundFlight?.filter?.priceRange?.max
        : data?.returnFlight?.filter?.priceRange?.max;
    setSliderValue(currentMax || 0);
    selectedPriceRange.current = currentMax || 0;

    // Reset Redux filters
    dispatch(resetFilter());

    // Clear session storage
    sessionStorage.removeItem('onwardFilter');
    sessionStorage.removeItem('returnFilter');

    // Reapply empty filters to update the view
    if (tabIndex === 0) {
      applyFiler(
        'APPLY_FILTER',
        [], // empty stops
        [], // empty departure times
        [], // empty airlines
        [], // reset arrival time
        [0, currentMax || 0],
      );
    } else {
      applyFiler('APPLY_RETURN_FILTER', [], [], [], [], [0, currentMax || 0]);
    }
  };
  // reset filter fields
  // useEffect(() => {
  //   handleResetFilter();
  // }, [flightListData]);

  // Update slider value when tab changes
  useEffect(() => {
    const currentMax =
      tabIndex === 0
        ? data?.outBoundFlight?.filter?.priceRange?.max
        : data?.returnFlight?.filter?.priceRange?.max;
    // Set filter values when tab changes
    const storedFilter =
      tabIndex === 0
        ? JSON.parse(sessionStorage.getItem('onwardFilter') || '{}')
        : JSON.parse(sessionStorage.getItem('returnFilter') || '{}');

    // Update local state with stored filter values
    setSelectedAirline(storedFilter.airline || []);
    setSelectedDeparture(storedFilter.departure_time || []);
    setSelectedStops(storedFilter.stops || []);
    selectedPriceRange.current =
      storedFilter.priceRange?.[1] || currentMax || 0;
    setSliderValue(currentMax || 0);
    if (tabIndex === 0) {
      applyFiler(
        'APPLY_FILTER',
        storedFilter.stops || [],
        storedFilter.departure_time || [],
        storedFilter.airline || [],
        [],
        [0, currentMax || 0],
      );
    } else {
      applyFiler(
        'APPLY_RETURN_FILTER',
        storedFilter.stops || [],
        storedFilter.departure_time || [],
        storedFilter.airline || [],
        [],
        [0, currentMax || 0],
      );
    }
  }, [tabIndex, data]);

  useEffect(() => {
    if (flightSearchInput?.travelMode === 'INRT') {
      dispatch(clearLocalState(userInput?.selectedReturnFlight));
    }
  }, [flightSearchInput, userInput?.selectedReturnFlight]);

  const applyFiler = (
    type: string,
    stops: string[] = [],
    departure_time: string[] = [],
    airline: string[] = [],
    arrival_time: string[] = [],
    priceRange: number[] = [],
  ) => {
    if (type === 'APPLY_FILTER') {
      sessionStorage.setItem(
        'onwardFilter',
        JSON.stringify({
          stops,
          departure_time,
          airline,
          arrival_time,
          priceRange,
        }),
      );

      // Apply filter and get filtered data
      const filteredData =
        onwardFlight?.length > 0
          ? filterFlights(onwardFlight, {
            stops,
            departure_time,
            airline,
            arrival_time,
            priceRange,
          })
          : [];

      dispatch(
        applyFilter({
          stops,
          departure_time,
          airline,
          arrival_time,
          priceRange,
          onwardFlight: onwardFlight,
        }),
      );

      // Update flightDetails with filtered data
      dispatch(setFilteredOnwardData(filteredData));
    } else {
      sessionStorage.setItem(
        'returnFilter',
        JSON.stringify({
          stops,
          departure_time,
          airline,
          arrival_time,
          priceRange,
        }),
      );

      // Apply filter and get filtered data
      const filteredReturnData =
        returnFlight?.length > 0
          ? filterFlights(returnFlight, {
            stops,
            departure_time,
            airline,
            arrival_time,
            priceRange,
          })
          : [];

      dispatch(
        applyReturnFilter({
          stops,
          departure_time,
          airline,
          arrival_time,
          priceRange,
          returnFlight: returnFlight,
        }),
      );

      // Update flightDetails with filtered data
      dispatch(setFilteredReturnData(filteredReturnData));
    }
  };

  const onClickDeparture = (val: string) => {
    const newDepartures = selectedDeparture.includes(val)
      ? selectedDeparture.filter((item) => item !== val)
      : [...selectedDeparture, val];
    // Update the correct tab's departure times
    setSelectedDeparture(newDepartures);

    // Apply filters based on current tab and travel type
    if (tabIndex === 0) {
      // Apply outbound filter
      applyFiler(
        'APPLY_FILTER',
        selectedStop,
        newDepartures,
        selectedAirline,
        [],
        [0, selectedPriceRange.current],
      );

      // If it's a round trip, maintain return flight filters
      // if (flightSearchInput.travelType === 'RT') {
      //   applyFiler(
      //     'APPLY_RETURN_FILTER',
      //     returnStops, // Use return flight's current stops
      //     returnDeparture, // Use return flight's current departure times
      //     selectedAirline,
      //     [],
      //     [0, selectedPriceRange.current],
      //   );
      // }
    } else {
      // Apply return filter
      applyFiler(
        'APPLY_RETURN_FILTER',
        selectedStop,
        newDepartures,
        selectedAirline,
        [],
        [0, selectedPriceRange.current],
      );

      // Maintain outbound flight filters
      // applyFiler(
      //   'APPLY_FILTER',
      //   outboundStops, // Use outbound flight's current stops
      //   outboundDeparture, // Use outbound flight's current departure times
      //   selectedAirline,
      //   [],
      //   [0, selectedPriceRange.current],
      // );
    }
  };

  const onClickStops = (val: string) => {
    const newStops = selectedStop.includes(val)
      ? selectedStop.filter((item) => item !== val)
      : [...selectedStop, val];

    // Update the correct tab's stops
    setSelectedStops(newStops);

    if (tabIndex === 0) {
      // Apply outbound filter
      applyFiler(
        'APPLY_FILTER',
        newStops,
        selectedDeparture,
        selectedAirline,
        [],
        [0, selectedPriceRange.current],
      );

      // If it's a round trip, maintain return flight filters
      // if (flightSearchInput.travelType === 'RT') {
      //   applyFiler(
      //     'APPLY_RETURN_FILTER',
      //     returnStops, // Use return flight's current stops
      //     returnDeparture, // Use return flight's current departure times
      //     selectedAirline,
      //     [],
      //     [0, selectedPriceRange.current],
      //   );
      // }
    } else {
      // Apply return filter
      applyFiler(
        'APPLY_RETURN_FILTER',
        newStops,
        selectedDeparture,
        selectedAirline,
        [],
        [0, selectedPriceRange.current],
      );

      // Maintain outbound flight filters
      // applyFiler(
      //   'APPLY_FILTER',
      //   outboundStops, // Use outbound flight's current stops
      //   outboundDeparture, // Use outbound flight's current departure times
      //   selectedAirline,
      //   [],
      //   [0, selectedPriceRange.current],
      // );
    }
  };

  const onChangeCheckBox = (val: string) => {
    const newAirlines = selectedAirline.includes(val)
      ? selectedAirline.filter((item) => item !== val)
      : [...selectedAirline, val];

    setSelectedAirline(newAirlines);

    // Apply filters to both outbound and return flights as needed
    if (tabIndex === 0) {
      // Apply to outbound flight
      applyFiler(
        'APPLY_FILTER',
        selectedStop,
        selectedDeparture,
        newAirlines,
        [],
        [0, selectedPriceRange.current],
      );

      // If round trip, maintain return flight filters
      // if (flightSearchInput.travelType === 'RT') {
      //   applyFiler(
      //     'APPLY_RETURN_FILTER',
      //     returnStops,
      //     returnDeparture,
      //     newAirlines,
      //     [],
      //     [0, selectedPriceRange.current],
      //   );
      // }
    } else {
      // Apply to return flight
      applyFiler(
        'APPLY_RETURN_FILTER',
        selectedStop,
        selectedDeparture,
        newAirlines,
        [],
        [0, selectedPriceRange.current],
      );

      // Maintain outbound flight filters
      // applyFiler(
      //   'APPLY_FILTER',
      //   outboundStops,
      //   outboundDeparture,
      //   newAirlines,
      //   [],
      //   [0, selectedPriceRange.current],
      // );
    }
  };

  const onPriceRangeSlide = (value: number[]) => {
    const newValue = value[0];
    setSliderValue(newValue);
    selectedPriceRange.current = newValue;

    // Apply filter with new price range to both flights as needed
    if (tabIndex === 0) {
      // Apply to outbound flight
      applyFiler(
        'APPLY_FILTER',
        selectedStop,
        selectedDeparture,
        selectedAirline,
        [],
        [0, newValue],
      );

      // If round trip, maintain return flight filters
      // if (flightSearchInput.travelType === 'RT') {
      //   applyFiler(
      //     'APPLY_RETURN_FILTER',
      //     returnStops,
      //     returnDeparture,
      //     selectedAirline,
      //     [],
      //     [0, newValue],
      //   );
      // }
    } else {
      // Apply to return flight
      applyFiler(
        'APPLY_RETURN_FILTER',
        selectedStop,
        selectedDeparture,
        selectedAirline,
        [],
        [0, newValue],
      );

      // Maintain outbound flight filters
      // applyFiler(
      //   'APPLY_FILTER',
      //   outboundStops,
      //   outboundDeparture,
      //   selectedAirline,
      //   [],
      //   [0, newValue],
      // );
    }
  };

  const debouncedOnPriceRangeSlider = debounce(onPriceRangeSlide, 300);
  if (isLoading) {
    return <FilterSkeleton />;
  }

  return (
    <div className="sticky top-0 max-h-screen w-full overflow-y-auto rounded-lg border bg-white p-3 shadow">
      {flightSearchInput?.travelType === 'RT' &&
        flightSearchInput?.travelMode === 'DO' &&
        userInput?.selectedFlight?.origin &&
        userInput?.selectedFlight?.destination && (
          <Tabs
            defaultValue={
              userInput?.selectedFlight?.origin +
              '-' +
              userInput?.selectedFlight?.destination
            }
          >
            <TabsList className="mx-auto flex justify-center gap-3 p-0">
              {[userInput?.selectedFlight, userInput?.selectedReturnFlight].map(
                (flightData, index) => (
                  <TabsTrigger
                    key={flightData?.origin + '-' + flightData?.destination}
                    value={flightData?.origin + '-' + flightData?.destination}
                    onClick={() => {
                      setTabIndex(index);
                      // if (index === 0) {
                      //   applyFiler(
                      //     'APPLY_FILTER',
                      //     outboundStops,
                      //     outboundDeparture,
                      //     selectedAirline,
                      //     [],
                      //     [
                      //       data?.outBoundFlight?.filter?.0,
                      //       data?.outBoundFlight?.filter?.priceRange?.max || 0,
                      //     ],
                      //   );
                      // } else {
                      //   applyFiler(
                      //     'APPLY_RETURN_FILTER',
                      //     returnStops,
                      //     returnDeparture,
                      //     selectedAirline,
                      //     [],
                      //     [
                      //       data?.returnFlight?.filter?.0,
                      //       data?.returnFlight?.filter?.priceRange?.max || 0,
                      //     ],
                      //   );
                      // }
                    }}
                    className={`rounded-2xl ${tabIndex === index ? 'text-white data-[state=active]:bg-tabBackground' : 'text-primary data-[state=active]:bg-background'} sm:px-2 lg:px-5 lg:text-xs`}
                  >
                    {flightData?.origin && flightData?.destination
                      ? flightData?.origin + '-' + flightData?.destination
                      : ''}
                  </TabsTrigger>
                ),
              )}
            </TabsList>
          </Tabs>
        )}
      <div className="mt-2.5 flex flex-nowrap place-content-between items-center">
        <FilterTitle title="Filters" />
        <span
          className="cursor-pointer text-xs text-text_secondary"
          onClick={() => handleResetFilter()}
        >
          Reset All
        </span>
      </div>
      <span className="text-xs text-text_secondary">
        {`Showing ${flightListData?.length || 0} Flights`}
      </span>

      <div className="mb-4 mt-3 w-full border-b-2 border-gray-500"></div>

      <FilterTitle title="Departure" />
      <div className="m-2 grid grid-cols-2 gap-4">
        {departureTime?.late_night && (
          <div onClick={() => onClickDeparture('late_night')}>
            <FilterTabs
              title="Before 6AM"
              selected={selectedDeparture.includes('late_night')}
            />
          </div>
        )}
        {departureTime?.morning && (
          <div onClick={() => onClickDeparture('morning')}>
            <FilterTabs
              title="6AM to 12 PM"
              selected={selectedDeparture.includes('morning')}
            />
          </div>
        )}
        {departureTime?.mid_evn && (
          <div onClick={() => onClickDeparture('mid_evn')}>
            <FilterTabs
              title="12PM to 6PM"
              selected={selectedDeparture.includes('mid_evn')}
            />
          </div>
        )}
        {departureTime?.night && (
          <div onClick={() => onClickDeparture('night')}>
            <FilterTabs
              title="After 6PM"
              selected={selectedDeparture.includes('night')}
            />
          </div>
        )}
      </div>

      <div className="mb-4 mt-3 w-full border-b-2 border-gray-500"></div>

      <FilterTitle title="Stops" />
      <div className="m-2 grid grid-cols-2 gap-4">
        {stopsData &&
          Object.keys(stopsData)?.map((stop) => {
            if (stopsData[stop]) {
              return (
                <div
                  key={stop}
                  onClick={() =>
                    onClickStops(stop === 'zero' ? 'Non stop' : stop)
                  }
                >
                  <FilterTabs
                    title={
                      stop === 'zero'
                        ? 'Non stop'
                        : stop === 'one'
                          ? '1 stop'
                          : '2 stops'
                    }
                    selected={selectedStop?.includes(
                      stop === 'zero' ? 'Non stop' : stop,
                    )}
                  />
                </div>
              );
            }
            return null;
          })}
      </div>

      <div className="mb-4 mt-3 w-full border-b-2 border-gray-500"></div>
      <div>
        <FilterTitle title="Price" />
        <Slider
          onValueChange={debouncedOnPriceRangeSlider}
          defaultValue={[sliderValue]}
          value={[sliderValue]}
          min={Number(0)}
          max={Number(priceRange?.max || 0)}
          step={1}
          className="mt-4"
        />
        <div className="mt-1 flex justify-between">
          <h4 className="text-sm font-medium text-zinc-800">
            {formatCurrency(0)}
          </h4>
          <h4 className="text-sm font-medium text-zinc-800">
            {formatCurrency(sliderValue)}
          </h4>
        </div>
      </div>
      <div className="mb-4 mt-3 w-full border-b-2 border-gray-500"></div>

      <div className="mt-2.5 flex flex-nowrap place-content-between items-center">
        <FilterTitle title="Preferred Airlines" />
      </div>
      <div className="item-center flex-col gap-4">
        {airlines?.map((air: any) => {
          return (
            <div
              key={air.airline}
              className="mt-3 flex items-center justify-between space-x-2"
            >
              <div className="flex w-full flex-row items-center justify-between">
                <div
                  className="flex flex-row items-center gap-1"
                  onClick={() => onChangeCheckBox(air.airline)}
                >
                  <Checkbox
                    id="terms"
                    className="h-4 w-4 border border-black"
                    checked={selectedAirline?.includes(air.airline)}
                    onCheckedChange={(val) => onChangeCheckBox(air.airline)}
                  />
                  {/* <Image
                    alt="airline_logos"
                    src={require(`@/assets/airlines/${air.airline}.png`)}
                    width={56}
                    height={56}
                  /> */}
                  <Label
                    htmlFor="terms"
                    className="cursor-pointer text-sm font-medium leading-none text-black text-zinc-800 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {air.airline}({air.count})
                  </Label>
                </div>
                {/* <div>
                  <h4 className="text-sm font-medium text-zinc-800">
                    {formatCurrency(air.fare[0])}
                  </h4>
                </div> */}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Filter;

const FilterTabs = ({
  title,
  selected,
}: {
  title: string;
  selected: boolean;
}) => {
  return (
    <div
      className={`${!selected ? 'bg-background' : 'bg-tabBackground text-white'} cursor-pointer rounded-lg py-3 text-center text-xs font-medium`}
    >
      {title}
    </div>
  );
};

const FilterTitle = ({ title }: { title: string }) => {
  return <div className="text-md mb-4 font-bold text-zinc-800">{title}</div>;
};
